import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AmplitudeEvent } from '../types';
import { logEvent } from '../analytics';
import styles from './NewGroupMultiSelect.module.css';
import { Colors } from '../colors';
import general from '../resources/general.json';

const NONE_OF_THE_ABOVE = 'None of the above';

interface Choice {
    title: string;
    value: string;
}

// TODO: reconcile diff b/w infermedica multiselect and covid vaccine triage multiselect
export interface GroupSelectAttachmentData {
    actions: any[];
    body: {
        items: {
            choices: Choice[];
            id: string;
            isMultiSelect: boolean;
            style: string;
            type: string;
            action?: string;
            slot?: string;
            amplitudeEvent?: AmplitudeEvent;
        }[];
        type: string;
    }[];
    type: string;
    version: string;
}

const NewGroupMultiSelectAttachment: React.FC<{
    data: GroupSelectAttachmentData;
    disableButtons: boolean;
    sendMessage: (message: any) => void;
    sendPostBack: (message: any) => void;
}> = ({ data, sendMessage, sendPostBack }) => {
    const [optionsSelected, setOptionsSelected] = useState({});
    const choices = data.body[0].items[0].choices;

    const handleConfirm = () => {
        if (data.body[0].items[0].action && data.body[0].items[0].slot) {
            const selectedValues = Object.values(optionsSelected);
            const dataString = {
                [data.body[0].items[0].slot]: selectedValues,
            };
            const amplitudeEvent = data.body[0].items[0].amplitudeEvent;
            if (amplitudeEvent) {
                logEvent(
                    amplitudeEvent.eventType || '',
                    amplitudeEvent.eventProperties || {}
                );
            }
            sendPostBack({
                text: `${data.body[0].items[0].action}${JSON.stringify(
                    dataString
                )}`,
                displayText: Object.values(optionsSelected),
            });
            return;
        }
        sendMessage(Object.values(optionsSelected).join('|'));
    };

    const checkboxElts = choices.map((choice) => {
        const handleChange = () => {
            if (
                choice.title === NONE_OF_THE_ABOVE &&
                !(optionsSelected as any)[choice.title]
            ) {
                setOptionsSelected({ [choice.title]: choice.value });
                return;
            }
            const newOptions = { ...optionsSelected } as any;
            if (choice.title in optionsSelected) {
                delete newOptions[choice.title];
            } else {
                newOptions[choice.title] = choice.value;
            }
            if (NONE_OF_THE_ABOVE in newOptions) {
                delete newOptions[NONE_OF_THE_ABOVE];
            }
            setOptionsSelected(newOptions);
        };
        const checked = choice.title in optionsSelected;
        const checkboxElt = (
            <Checkbox
                checked={checked}
                onChange={handleChange}
                sx={{
                    color: Colors.PrimaryMain,
                    '&.Mui-checked': {
                        color: Colors.PrimarySelected,
                    },
                }}
            />
        );
        return (
            <FormControlLabel
                key={`multiSelectButton-${choice.title}`}
                control={checkboxElt}
                label={choice.title}
                sx={{
                    alignItems: 'start',
                    margin: '0',
                    marginBottom: '1rem',
                    color: checked
                        ? Colors.PrimarySelected
                        : Colors.PrimaryMain,
                    '>.MuiButtonBase-root': {
                        margin: '0',
                        marginRight: '0.5rem',
                        padding: '0',
                    },
                    '&:hover': {
                        '> *': {
                            color: Colors.PrimaryLight,
                            '&.Mui-checked': {
                                color: Colors.PrimaryLight,
                            },
                        },
                        color: Colors.PrimaryLight,
                    },
                }}
            />
        );
    });

    const hasOptionsSelected = Object.keys(optionsSelected).length >= 1;

    return (
        <div className={styles.container}>
            <FormGroup className={styles.checkboxContainer}>
                {checkboxElts}
            </FormGroup>
            <Button
                className={styles.confirmButton}
                variant="contained"
                disabled={!hasOptionsSelected}
                onClick={handleConfirm}
                sx={{
                    '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 51, 142, 0.4)',
                        color: 'white',
                    },
                    ':hover': {
                        backgroundColor: Colors.PrimaryLight,
                        boxShadow: 'none',
                    },
                }}
            >
                {general.providence.newGroupMultiSelectConfirm}
            </Button>
        </div>
    );
};

export const NewGroupMultiSelect = NewGroupMultiSelectAttachment;
